import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  FirstUseNotices, LanguageSelect, languageOptions, owedNotices, useEnabledLanguages,
  type EnabledLanguage,
} from '@drbell/ui';
import {
  api, ApiError, DEFAULT_MEAL_TIMES, type ConsentDocument, type MealTimes, type PatientSummary,
} from '../api.ts';
import {
  browserZone, chosenName, MEAL_NAME, MEALS, MODE_TEXT, modesOffered, outOfOrder, setupChoices, zoneChoices, zoneName,
  type Mode, type Who,
} from '../self-service.ts';
import { GetTheApp } from './GetTheApp.tsx';

/**
 * Setting up reminders on the website — the phone's first run, here.
 *
 * The same questions in the same order as the phone (apps/mobile
 * Onboarding.tsx): the notices first, on a first set-up; then who DrBell
 * is for — **Me**, or **Someone I look after**; then the name, language,
 * how the screen looks and the meal times; then `POST /api/patients`, the
 * one path that makes a record, with the same plan limit.
 *
 * Two things the website adds, because of who opens it:
 *
 *  - **I'm helping someone else** first explains invitations. Most helpers
 *    are invited by somebody who already uses DrBell; creating a second
 *    record for the same mother is the mistake to avoid. Setting one up
 *    here is for a person with no phone, or whose medicines you run.
 *  - **The time zone** is asked. The phone takes its own; a son in London
 *    setting up his mother in Chennai is the commonest web case, and his
 *    browser's zone is the wrong one.
 *
 * And the truth the phone never needed to say: the reminders ring on a
 * phone, so the last step is getting the app.
 */
export interface SetUpProps {
  user: { id: string; name: string | null; phoneNumber: string | null; locale?: string };
  patients: readonly PatientSummary[];
  /** Where to start: the question, the helper's explanation, or a form. */
  start?: 'choose' | 'helping';
  /** A record was made: open it, on this tab. */
  onCreated: (patientId: string, tab: 'today' | 'medicines') => void;
  /** Present when there is somewhere to go back to — not on a first set-up. */
  onCancel?: () => void;
  /** Flags from the control plane: a screen look switched off there is not offered. */
  flags: Record<string, unknown>;
  /** Say "get the app" when done. Off where the page already says it beside this (the welcome page). */
  appCard?: boolean;
}

type Step = 'choose' | 'helping' | 'notices' | 'form' | 'done';

export function SetUp({ user, patients, start = 'choose', onCreated, onCancel, flags, appCard = true }: SetUpProps) {
  const firstRun = patients.length === 0;
  const choices = setupChoices(patients);
  const [step, setStep] = useState<Step>(choices.length === 1 && start === 'choose' ? 'helping' : start);
  const [who, setWho] = useState<Who>('me');
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  /*
   * The notices, on a first set-up — what DrBell is, the data it keeps, and
   * what it needs from a phone, asked before any of it happens (the phone's
   * Onboarding). In the account's language, with the English one click
   * away; agreeing records the language actually shown. Adding somebody
   * later does not ask again: the first medicine still would.
   */
  const [owed, setOwed] = useState<ConsentDocument[] | null>(firstRun ? null : []);
  const locale = user.locale ?? 'en-IN';
  useEffect(() => {
    if (!firstRun) return;
    api
      .get<{ documents: ConsentDocument[] }>(`/api/consent?locale=${encodeURIComponent(locale)}`)
      .then((r) => setOwed(owedNotices(r.documents).length > 0 ? r.documents : []))
      // Could not be fetched: the medicine dialog asks again, so nothing is skipped for good.
      .catch(() => setOwed([]));
  }, [firstRun, locale]);

  // How they signed in, to say "sign in to the app with the same".
  useEffect(() => {
    if (user.phoneNumber) return;
    api.get<{ email: string | null }>('/api/me/profile').then((p) => setEmail(p.email)).catch(() => undefined);
  }, [user.phoneNumber]);
  const how = user.phoneNumber ?? email;

  const begin = (w: Who) => {
    setWho(w);
    setStep(owed && owed.length > 0 ? 'notices' : 'form');
  };

  if (step === 'done' && created) {
    return (
      <Shell>
        <h2 className="text-xl font-semibold">
          {who === 'me' ? 'Your reminders are set up' : `Reminders are set up for ${created.name}`}
        </h2>
        <p className="mt-1 text-sm text-(--muted)">
          Next, add the medicines from the prescription. Then install the app, so the reminders ring.
        </p>
        {appCard && (
          <div className="mt-4">
            <GetTheApp how={how} />
          </div>
        )}
        {who === 'other' && (
          <p className="mt-4 text-sm text-(--muted)">
            Reminders for {created.name} ring on your phone until {created.name} has their own: invite their number
            as &ldquo;Their own phone&rdquo; in <strong className="text-(--fg)">Who looks after them</strong>.
          </p>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button onClick={() => onCreated(created.id, 'today')} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Later
          </button>
          <button
            onClick={() => onCreated(created.id, 'medicines')}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
          >
            Add a medicine
          </button>
        </div>
      </Shell>
    );
  }

  if (step === 'notices' && owed) {
    return (
      <Shell>
        <FirstUseNotices
          documents={owed}
          loadEnglish={async () => (await api.get<{ documents: ConsentDocument[] }>('/api/consent?locale=en-IN')).documents}
          accept={(documentId, version, shownIn) => api.post('/api/consent/accept', { documentId, version, locale: shownIn })}
          onAccepted={() => { setOwed([]); setStep('form'); }}
          onBack={() => setStep(who === 'other' ? 'helping' : 'choose')}
        />
      </Shell>
    );
  }

  if (step === 'form') {
    return (
      <Shell>
        <PersonForm
          who={who}
          user={user}
          flags={flags}
          onBack={() => setStep(who === 'other' ? 'helping' : 'choose')}
          onCreated={(id, name) => { setCreated({ id, name }); setStep('done'); }}
        />
      </Shell>
    );
  }

  if (step === 'helping') {
    return (
      <Shell>
        <h2 className="text-xl font-semibold">Helping someone else</h2>
        <p className="mt-1 text-sm text-(--muted)">There are two ways, depending on who sets the reminders up.</p>

        <div className="mt-4 rounded-xl border border-(--line) p-4">
          <p className="font-medium">They already use DrBell: ask them to invite you</p>
          <p className="mt-1 text-sm text-(--muted)">
            Whoever looks after their record adds you, in the DrBell app under{' '}
            <strong className="text-(--fg)">Medicines → Who looks after them</strong>, or on this website under{' '}
            <strong className="text-(--fg)">Who looks after them</strong>. They choose what you are: a caregiver who
            is told about missed doses, a guardian who also changes medicines, or a viewer.
          </p>
          {user.phoneNumber ? (
            <p className="mt-2 text-sm">
              Give them this number: <strong>{user.phoneNumber}</strong>. The invitation appears on this page, to
              approve or reject, as soon as it is sent.
            </p>
          ) : (
            <p className="mt-2 text-sm">
              Invitations are sent to a mobile number. Add yours in <strong>Your account</strong> first, so an
              invitation can find you; it then appears on this page, to approve or reject.
            </p>
          )}
        </div>

        <div className="mt-3 rounded-xl border border-(--line) p-4">
          <p className="font-medium">They have no phone, or you run their medicines: set them up here</p>
          <p className="mt-1 text-sm text-(--muted)">
            A parent, a child, or anyone else. You keep their record; you can invite them, and other family,
            afterwards. Their reminders ring on your phone until they have their own.
          </p>
          <button
            onClick={() => begin('other')}
            className="mt-3 min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
          >
            Set up someone&rsquo;s reminders
          </button>
        </div>

        {(() => {
          // Back to the question when it was asked; otherwise out, when there is an out.
          const back = choices.length > 1 && start === 'choose' ? () => setStep('choose') : onCancel;
          return back ? (
            <div className="mt-5 flex justify-end">
              <button onClick={back} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Back</button>
            </div>
          ) : null;
        })()}
      </Shell>
    );
  }

  // Who is DrBell for?
  return (
    <Shell>
      <h2 className="text-xl font-semibold">Who is DrBell for?</h2>
      <div className="mt-4 space-y-3">
        {choices.includes('me') && (
          <Choice
            title="Set up my own reminders"
            body="I take the medicines. I set up my times here, and the reminders ring in the DrBell app on my phone."
            onClick={() => begin('me')}
          />
        )}
        <Choice
          title="I'm helping someone else"
          body="A parent, a child, or anyone else whose medicines you look after."
          onClick={() => setStep('helping')}
        />
      </div>
      {onCancel && (
        <div className="mt-5 flex justify-end">
          <button onClick={onCancel} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Back</button>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return <div className="rounded-2xl border border-(--line) bg-(--panel) p-5">{children}</div>;
}

function Choice({ title, body, onClick }: { title: string; body: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="block min-h-11 w-full rounded-xl border-2 border-(--line) bg-(--bg) p-4 text-left hover:border-(--accent)"
    >
      <span className="block text-lg font-semibold">{title}</span>
      <span className="mt-1 block text-sm text-(--muted)">{body}</span>
    </button>
  );
}

/** The name, language, time zone, screen and meal times — then `POST /api/patients`. */
function PersonForm({ who, user, flags, onBack, onCreated }: {
  who: Who;
  user: SetUpProps['user'];
  flags: Record<string, unknown>;
  onBack: () => void;
  onCreated: (id: string, name: string) => void;
}) {
  const isMe = who === 'me';
  const thisZone = browserZone();
  // Not the phone number sign-up stored as the name.
  const [name, setName] = useState(isMe ? chosenName(user.name, user.phoneNumber) ?? '' : '');
  const [locale, setLocale] = useState(user.locale ?? 'en-IN');
  const [timezone, setTimezone] = useState(thisZone);
  const [mode, setMode] = useState<Mode>('standard');
  const [meals, setMeals] = useState<MealTimes>({ ...DEFAULT_MEAL_TIMES });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ title: string; body: string } | null>(null);

  const loadLanguages = useCallback(() => api.get<{ locales: EnabledLanguage[] }>('/api/i18n/locales'), []);
  const enabled = useEnabledLanguages(loadLanguages);
  const languages = useMemo(() => languageOptions(enabled, locale), [enabled, locale]);
  const zones = useMemo(() => zoneChoices(timezone, thisZone), [timezone, thisZone]);
  const label = isMe ? 'Your name' : 'Their name';
  const field = 'mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3';

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>('/api/patients', {
        displayName: name.trim(),
        isSelf: isMe,
        timezone,
        locale,
        presentationMode: mode,
        mealTimes: meals,
      });
      if (isMe) {
        // One person, one language: the account and its own record agree,
        // or the phone reverts at its next launch (defect 114).
        await api.put('/api/me/profile', { locale, name: name.trim() }).catch(() => undefined);
      }
      onCreated(id, name.trim());
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 402
          ? { title: 'Your plan’s limit', body: 'Your plan already looks after as many people as it includes. Nothing has been changed.' }
          : { title: 'Something went wrong', body: e instanceof Error ? e.message : String(e) },
      );
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (name.trim()) void create(); }}
      aria-labelledby="setup-title"
    >
      <h2 id="setup-title" className="text-xl font-semibold">
        {isMe ? 'Your reminders' : 'Their reminders'}
      </h2>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-(--muted)">{label}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus={!name} maxLength={120} className={field} />
      </label>

      <div className="mt-4">
        <span className="text-xs font-medium text-(--muted)">Language</span>
        <LanguageSelect className="mt-1" value={locale} options={languages} onChange={setLocale} />
        <span className="mt-1 block text-xs text-(--muted)">
          Reminders and read-aloud use this language. Medicine names always stay exactly as the doctor wrote them.
        </span>
      </div>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-(--muted)">Time zone</span>
        <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={field}>
          {zones.map((z) => <option key={z} value={z}>{zoneName(z)}{z === thisZone ? ' — this computer' : ''}</option>)}
        </select>
        <span className="mt-1 block text-xs text-(--muted)">
          {isMe ? 'Where you are.' : 'Where they live, not where you are.'} Doses follow this time zone.
        </span>
      </label>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-(--muted)">How the screen looks</legend>
        <ModeChoice value={mode} onChange={setMode} modes={modesOffered(flags)} />
      </fieldset>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-(--muted)">Meal times</legend>
        <p className="text-xs text-(--muted)">
          Doses written as 1-0-1 or &ldquo;after food&rdquo; follow these. Set them to when{' '}
          {name.trim() || (isMe ? 'you' : 'they')} actually {isMe || !name.trim() ? 'eat' : 'eats'}.
        </p>
        <MealFields value={meals} onChange={setMeals} />
        {outOfOrder(meals) && (
          <p className="mt-2 text-xs text-(--muted)">
            These times do not run breakfast, lunch, dinner, bedtime. That is fine for a night shift.
          </p>
        )}
      </fieldset>

      {error && (
        <div role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          <p className="font-medium">{error.title}</p>
          <p>{error.body}</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onBack} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Back</button>
        <button
          type="submit"
          disabled={busy || name.trim().length === 0}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Starting…' : 'Start reminders'}
        </button>
      </div>
    </form>
  );
}

/** Large and simple / Standard / For a child — radios, as the phone's ModeField. */
export function ModeChoice({ value, onChange, modes, disabled }: {
  value: Mode;
  onChange: (m: Mode) => void;
  modes: readonly Mode[];
  disabled?: boolean;
}) {
  return (
    <div className="mt-1 space-y-2" role="radiogroup">
      {modes.map((m) => (
        <label
          key={m}
          className={`flex min-h-11 cursor-pointer gap-3 rounded-lg border-2 p-3 ${
            m === value ? 'border-(--accent) bg-(--accent-soft)' : 'border-(--line) bg-(--bg)'
          } ${disabled ? 'cursor-default opacity-60' : ''}`}
        >
          <input
            type="radio"
            name="mode"
            checked={m === value}
            disabled={disabled}
            onChange={() => onChange(m)}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-medium">{MODE_TEXT[m].title}</span>
            <span className="block text-xs text-(--muted)">{MODE_TEXT[m].body}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

/** The four meals, each a time input. */
export function MealFields({ value, onChange, disabled }: {
  value: MealTimes;
  onChange: (next: MealTimes) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {MEALS.map((meal) => (
        <label key={meal} className="block">
          <span className="text-xs text-(--muted)">{MEAL_NAME[meal]}</span>
          <input
            type="time"
            value={value[meal]}
            disabled={disabled}
            onChange={(e) => e.target.value && onChange({ ...value, [meal]: e.target.value })}
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3"
          />
        </label>
      ))}
    </div>
  );
}
